/**
 * Document type icon — a small CSS-only "page with corner fold" glyph
 * tinted by file type. PDF = red, DOCX = blue, unknown = stone.
 *
 * Used in the import landing's picked-file grid and in the batch
 * detail view's file cards, so the user can scan a list of mixed
 * filetypes by colour alone.
 */

const PDF_MIME = 'application/pdf';
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export function FileTypeIcon({
  mime,
  size = 'md',
}: {
  mime: string | null | undefined;
  size?: 'sm' | 'md' | 'lg';
}) {
  const isPdf = mime === PDF_MIME || (mime ?? '').endsWith('pdf');
  const isDocx =
    mime === DOCX_MIME ||
    (mime ?? '').endsWith('docx') ||
    (mime ?? '').includes('wordprocessing');

  const label = isPdf ? 'PDF' : isDocx ? 'DOCX' : 'DOC';
  const tint = isPdf
    ? { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', fold: 'bg-red-100' }
    : isDocx
      ? { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', fold: 'bg-sky-100' }
      : { bg: 'bg-stone-50', border: 'border-stone-200', text: 'text-stone-600', fold: 'bg-stone-100' };

  const dims =
    size === 'lg'
      ? { box: 'h-14 w-11', text: 'text-[11px]', fold: 'h-3 w-3' }
      : size === 'sm'
        ? { box: 'h-8 w-7', text: 'text-[8px]', fold: 'h-2 w-2' }
        : { box: 'h-10 w-8', text: 'text-[9px]', fold: 'h-2.5 w-2.5' };

  return (
    <div
      aria-hidden
      className={`relative flex shrink-0 items-end justify-center rounded-sm border ${tint.bg} ${tint.border} ${dims.box}`}
    >
      {/* Corner fold */}
      <div
        className={`absolute right-0 top-0 ${tint.fold} ${dims.fold}`}
        style={{ clipPath: 'polygon(0 0, 100% 100%, 100% 0)' }}
      />
      <span className={`mb-0.5 font-semibold tracking-wider ${tint.text} ${dims.text}`}>
        {label}
      </span>
    </div>
  );
}
